/**
 * WorkforceFloor — Aperture Bank interaction atlas §1 (F1–F8), stage 1 of the
 * atlas port. ONE screen, four tabs — live / analytics / audit / settings —
 * matching the capture; the tab strip is the seam between "act now" and
 * "act later". Previously this was two screens (Floor + Manager Assist); the
 * assist surfaces moved in here. `workforce.assists` still routes to the old
 * WorkforceAssist screen — retiring that route runs through the deletion-impact
 * review at stage 9, not ad hoc here.
 *
 * Ops (all kit-served): getFloorBoard · resolveFloorAlert · getLiveQueues ·
 * listMarketplaceShifts · getDemandForecast · getAssistAnalytics ·
 * listAssistEvents · getAssistSettings · updateAssistSettings.
 *
 * Held pending the data layer (REQ-097, nothing faked): per-remedy move plans
 * in the workbench, Save draft / Snooze, prior-period KPI deltas, urgency tags
 * on audit rows, reason presets, notification sound.
 *
 * Conditional chrome (atlas §9 trap): the period toggle renders on Analytics
 * and Audit only — a period means nothing on Live or Settings.
 */
import { useMemo, useState } from "react";
import { Btn, Icon, Pill, T, alpha } from "@v0y4ge/jrni-ui";
import { Screen } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { EmptyState } from "../../shell/composites";
import { toast } from "../../shell/toast";
import { can, useUser } from "../../shell/access";
import { useViewport } from "../../shell/useResponsive";
import { useWfResource, useBusy } from "./useWorkforce";
import { WfPending, hmToMin, isoToday, mondayOf } from "./wf-shared";
import {
  getFloorBoard, resolveFloorAlert, sessionScope, getLiveQueues, listMarketplaceShifts,
  getDemandForecast, getAssistAnalytics, listAssistEvents, getAssistSettings, updateAssistSettings,
  type FloorBoardResponse, type FloorAlert, type AssistAnalytics, type AssistEvent, type AssistSettings,
} from "./wf-api";
import { useT } from "../../lib/i18n";

type Tab = "live" | "analytics" | "audit" | "settings";
type Period = "today" | "7d" | "30d";
const PERIODS: { id: Period; label: string; labelKey: string }[] = [
  { id: "today", label: "Today", labelKey: "workforce.assist.today" },
  { id: "7d", label: "7 days", labelKey: "workforce.assist.text7Days" },
  { id: "30d", label: "30 days", labelKey: "workforce.assist.text30Days" },
];
const fmtTtr = (sec: number | null) => (sec == null || !sec ? "—" : sec < 60 ? `${sec}s` : `${Math.floor(sec / 60)}m ${String(sec % 60).padStart(2, "0")}s`);
const sevColor = (s: 1 | 2 | 3) => (s >= 3 ? T.red1 : s === 2 ? T.orange1 : T.tOrange);
const clock = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });

/* ── Shared bits ─────────────────────────────────────────────────────── */

function Tile({ accent, label, labelTone, children }: { accent: string; label: string; labelTone?: string; children: React.ReactNode }) {
  return (
    <div style={{ padding: "13px 16px", background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, position: "relative", overflow: "hidden", display: "flex", flexDirection: "column", gap: 3 }}>
      <span style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: accent }} />
      <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.07, color: labelTone ?? T.soft, display: "flex", alignItems: "center", gap: 5 }}>{label}</span>
      {children}
    </div>
  );
}
const bigNum: React.CSSProperties = { fontSize: 26, fontWeight: 700, color: T.n1, fontFamily: T.fontMono, lineHeight: 1.1 };
const tileFoot: React.CSSProperties = { fontSize: 11, color: T.soft };

function TabBtn({ active, onClick, children, testid }: { active: boolean; onClick: () => void; children: React.ReactNode; testid?: string }) {
  return (
    <button data-testid={testid} onClick={onClick} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 15px", minHeight: 34, borderRadius: 7, border: "none", background: active ? T.p1 : "transparent", color: active ? T.white : T.soft, fontSize: 12.5, fontWeight: active ? 700 : 600, cursor: "pointer", fontFamily: T.font }}>{children}</button>
  );
}
function Chip({ active, onClick, children, disabled, title }: { active: boolean; onClick: () => void; children: React.ReactNode; disabled?: boolean; title?: string }) {
  return (
    <button onClick={onClick} disabled={disabled} title={title} style={{ padding: "6px 13px", minHeight: 32, borderRadius: 999, border: `1px solid ${active ? T.p1 : T.n5}`, background: active ? T.p1 : T.white, color: active ? T.white : T.n2, fontSize: 12, fontWeight: active ? 700 : 500, cursor: disabled ? "not-allowed" : "pointer", fontFamily: T.font, opacity: disabled ? 0.55 : 1 }}>{children}</button>
  );
}
function Card({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.white, overflow: "hidden", flexShrink: 0, ...style }}>{children}</div>;
}
function CardHead({ title, sub, right }: { title: string; sub?: string; right?: React.ReactNode }) {
  return (
    <div style={{ padding: "14px 18px 12px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
      <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <b style={{ fontSize: 14.5, color: T.n1 }}>{title}</b>
        {sub ? <span style={{ fontSize: 11.5, color: T.soft }}>{sub}</span> : null}
      </span>
      {right}
    </div>
  );
}

/* ── Escalation banner (F1–F8 — present on every tab) ─────────────────── */

function EscalationBanner({ ev, escalateAfterSec, onOpenTray, onDismiss }: {
  ev: AssistEvent; escalateAfterSec: number | null; onOpenTray: () => void; onDismiss: () => void;
}) {
  const t = useT();
  const waited = Math.max(0, Math.round((Date.now() - new Date(ev.ts).getTime()) / 1000));
  const left = escalateAfterSec == null ? null : Math.max(0, escalateAfterSec - waited);
  return (
    <div data-testid="wffloor-escalation" style={{ display: "flex", alignItems: "center", gap: 14, padding: "11px 22px", background: T.red1, color: T.white }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: T.white, flexShrink: 0 }} />
      <span style={{ display: "flex", flexDirection: "column", gap: 1, flex: 1, minWidth: 0 }}>
        <b style={{ fontSize: 13 }}>{ev.fromName}{t("workforce.floor.atNeedsYou", " at ")}{ev.location}{t("workforce.floor.needsYou", " needs you")}</b>
        <span style={{ fontSize: 11.5, opacity: 0.92 }}>
          {ev.reason}{" · "}{t("workforce.floor.waiting", "waiting")}{" "}{waited}s
          {left != null ? ` · ${left > 0 ? `${t("workforce.floor.escalatesIn", "escalates in")} ${left}s` : t("workforce.floor.escalatedToFloorLead", "escalated to floor lead")}` : ""}
        </span>
      </span>
      <Btn size="sm" variant="secondary" testid="wffloor-open-tray" onClick={onOpenTray}>{t("workforce.floor.openTray", "Open tray")} ›</Btn>
      <button disabled title={t("workforce.floor.resolvingAnAssistFromTheBannerNeedsReq097d", "Resolving an assist from the banner needs REQ-097d")} style={{ width: 30, height: 30, borderRadius: 7, border: `1px solid ${alpha(T.white, 45)}`, background: "transparent", color: T.white, cursor: "not-allowed", opacity: 0.6 }}><Icon.check size={14} /></button>
      <button onClick={onDismiss} aria-label={t("common.actions.dismiss", "Dismiss")} style={{ width: 30, height: 30, borderRadius: 7, border: "none", background: "transparent", color: T.white, cursor: "pointer" }}><Icon.x size={14} /></button>
    </div>
  );
}

/* ── Live (F1, F2) ────────────────────────────────────────────────────── */

const REMEDIES = [
  { id: "reflow", label: "Re-flow the schedule", labelKey: "workforce.floor.reFlowTheSchedule" },
  { id: "marketplace", label: "Post to marketplace", labelKey: "workforce.floor.postToMarketplace" },
  { id: "phone", label: "Push to phone channel", labelKey: "workforce.floor.pushToPhoneChannel" },
  { id: "lunch", label: "Delay a lunch break", labelKey: "workforce.floor.delayALunchBreak" },
];

function LiveTab({ board, locationId, onReload }: { board: FloorBoardResponse; locationId: string; onReload: () => void }) {
  const t = useT();
  const phone = useViewport() === "phone";
  const { busy, run } = useBusy();
  const canAct = can("wfops.write");
  const [selected, setSelected] = useState<string | null>(null);
  const [remedy, setRemedy] = useState(REMEDIES[0].id);

  const queues = useWfResource("live-queues", getLiveQueues, { pollMs: 30000 });
  const market = useWfResource(`mkt:${locationId}`, () => listMarketplaceShifts({ locationId }));
  const demand = useWfResource(`demand:${locationId}`, () => getDemandForecast(mondayOf(), locationId));

  const critical = board.alerts.filter((a) => a.severity >= 3).length;
  const solo = board.coverage.filter((z) => z.present === 1).length;
  const hot = critical > 0 || board.alerts.length >= 3;

  const branchQueue = queues.data?.queues.find((q) => q.branchId === locationId);
  const overSla = branchQueue ? branchQueue.waiting.filter((w) => w.waitMin > w.slaMin).length : null;

  const poolHrs = useMemo(() => {
    const open = (market.data?.shifts ?? []).filter((s) => s.status === "open");
    return open.reduce((sum, s) => sum + Math.max(0, hmToMin(s.end) - hmToMin(s.start)) / 60, 0);
  }, [market.data]);

  const deficit = useMemo(() => {
    const today = demand.data?.days.find((d) => d.date === isoToday());
    if (!today) return null;
    const nextHour = new Date().getHours() + 1;
    const inHour = today.slices.filter((s) => Math.floor(hmToMin(s.t) / 60) === nextHour);
    if (inHour.length === 0) return null;
    const need = inHour.reduce((m, s) => Math.max(m, s.events + s.appointments + s.queue), 0);
    const present = board.coverage.reduce((sum, z) => sum + z.present, 0);
    return present - need;
  }, [demand.data, board.coverage]);

  const alert = board.alerts.find((a) => a.id === selected) ?? board.alerts[0] ?? null;

  const resolve = (a: FloorAlert) => {
    void run(`${a.id}:accept`, async () => {
      try {
        const r = await resolveFloorAlert(a.id, { action: "accept-suggestion" });
        toast({ tone: "success", title: r.applied ? t("workforce.floor.suggestionApplied", "Suggestion applied") : t("workforce.floor.alertResolved", "Alert resolved"), subtitle: a.title });
        onReload();
      } catch (e) { toast({ tone: "danger", title: t("workforce.floor.couldnTResolveTheAlert", "Couldn't resolve the alert"), subtitle: e instanceof Error ? e.message : String(e) }); }
    });
  };

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: phone ? "1fr" : "repeat(auto-fit, minmax(210px, 1fr))", gap: 12 }}>
        <Tile accent={hot ? T.red1 : T.green1} labelTone={hot ? T.red1 : T.green1}
          label={`${hot ? t("workforce.floor.floorIsHot", "FLOOR IS HOT") : t("workforce.floor.floorIsSteady", "FLOOR IS STEADY")} · ${board.asOf}`}>
          <span style={{ fontSize: 15.5, fontWeight: 700, color: T.n1, lineHeight: 1.3 }}>
            {board.alerts.length} {t("workforce.floor.liveAlerts", "live alerts")} · {critical} {t("workforce.floor.critical", "critical")} · {solo} {t("workforce.floor.actingSolo", "acting solo")}
          </span>
          <span style={tileFoot}>{t("workforce.floor.workbenchKeepsEveryMoveInOnePlace", "Workbench keeps every move in one place — re-flow, marketplace, channel push, break delays.")}</span>
        </Tile>
        <Tile accent={T.tOrange} label={t("workforce.floor.waitSla", "WAIT › SLA")}>
          <span style={bigNum}>{queues.loading ? "…" : overSla ?? "—"}</span>
          <span style={tileFoot}>{branchQueue ? `${t("workforce.floor.of", "of")} ${branchQueue.waiting.length} ${t("workforce.floor.inQueue", "in queue")}` : t("workforce.floor.noQueueDataForThisBranch", "no queue data for this branch")}</span>
        </Tile>
        <Tile accent={T.orange1} label={t("workforce.floor.slotDeficitNextHr", "SLOT DEFICIT · NEXT HR")}>
          <span style={{ ...bigNum, color: deficit != null && deficit < 0 ? T.red1 : T.n1 }}>{demand.loading ? "…" : deficit == null ? "—" : deficit > 0 ? `+${deficit}` : String(deficit)}</span>
          <span style={tileFoot}>{deficit == null ? t("workforce.floor.noForecastForTheNextHour", "no forecast for the next hour") : t("workforce.floor.capacityVsForecast", "capacity vs forecast")}</span>
        </Tile>
        <Tile accent={T.green1} label={t("workforce.floor.marketplacePool", "MARKETPLACE POOL")}>
          <span style={bigNum}>{market.loading ? "…" : `${Math.round(poolHrs)} ${t("workforce.floor.hrs", "hrs")}`}</span>
          <span style={tileFoot}>{t("workforce.floor.claimableAdjacentBranches", "claimable · adjacent branches")}</span>
        </Tile>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: phone ? "1fr" : "repeat(auto-fit, minmax(360px, 1fr))", gap: 16, alignItems: "start" }}>
        <Card>
          <CardHead title={t("workforce.floor.liveAlerts2", "Live alerts")} sub={t("workforce.floor.eachOneExpandsIntoTheWorkbench", "Each one expands into the workbench right · resolve one move at a time")}
            right={<Pill tone="neutral">{t("workforce.floor.managerAssist", "Manager Assist")}</Pill>} />
          <div style={{ padding: "0 14px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
            {board.alerts.length === 0 ? (
              <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "13px 16px", borderRadius: 10, background: alpha(T.green1, 10) }}>
                <Icon.check size={14} style={{ color: T.green1 }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{t("workforce.floor.allQuietNoActiveFloorAlerts", "All quiet — no active floor alerts.")}</span>
              </div>
            ) : board.alerts.map((a) => {
              const accent = sevColor(a.severity);
              const on = alert?.id === a.id;
              return (
                <button key={a.id} data-testid="wffloor-alert" data-id={a.id} onClick={() => setSelected(a.id)}
                  style={{ textAlign: "left", display: "flex", flexDirection: "column", gap: 4, padding: "11px 14px", minHeight: 44, borderRadius: 8, border: "none", borderLeft: `3px solid ${accent}`, background: on ? alpha(accent, 12) : alpha(accent, 5), cursor: "pointer", fontFamily: T.font }}>
                  <span style={{ display: "flex", gap: 9, alignItems: "baseline", flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11.5, fontFamily: T.fontMono, fontWeight: 700, color: accent }}>{a.sinceMin}m</span>
                    <b style={{ fontSize: 13, color: T.n1 }}>{a.title}</b>
                  </span>
                  <span style={{ fontSize: 12, color: T.n2, lineHeight: 1.45 }}>{a.detail}</span>
                  <span style={{ fontSize: 11, color: accent, fontWeight: 600 }}>{a.suggestedAction}</span>
                </button>
              );
            })}
          </div>
        </Card>

        <Card>
          <CardHead title={t("workforce.floor.workbench", "Workbench")} sub={t("workforce.floor.resolveAHotAlertWithoutLeavingThisPane", "Resolve a hot alert without leaving this pane")}
            right={<Pill tone="primary">{board.alerts.length} {t("workforce.floor.alertOpen", "open")}</Pill>} />
          {!alert ? (
            <div style={{ padding: "8px 18px 20px", fontSize: 12.5, color: T.soft }}>{t("workforce.floor.nothingToResolveRightNow", "Nothing to resolve right now.")}</div>
          ) : (
            <div style={{ padding: "0 18px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                {REMEDIES.map((r) => (
                  <Chip key={r.id} active={remedy === r.id} onClick={() => setRemedy(r.id)}>{t(r.labelKey, r.label)}</Chip>
                ))}
              </div>
              <div style={{ border: `1px solid ${T.n5}`, borderRadius: 9, padding: "13px 15px", display: "flex", flexDirection: "column", gap: 9 }}>
                <b style={{ fontSize: 13, color: T.n1 }}>{alert.title}</b>
                <span style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
                  <Icon.check size={13} style={{ color: T.green1, marginTop: 2, flexShrink: 0 }} />
                  <span style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.5 }}>{alert.suggestedAction}</span>
                </span>
                <span style={{ fontSize: 11, color: T.soft, lineHeight: 1.45 }}>
                  {t("workforce.floor.remedyPlansArentServedYet", "Per-remedy move plans aren't served yet — the board returns one suggested move per alert (REQ-097a).")}
                </span>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <Btn testid="wffloor-apply" disabled={!canAct || busy !== null}
                  title={canAct ? undefined : t("workforce.workforceFloor.requiresTheWfopsWriteCapability", "Requires the wfops.write capability")}
                  onClick={() => resolve(alert)}>
                  {busy === `${alert.id}:accept` ? t("workforce.workforceFloor.applying", "Applying…") : `✓ ${t("workforce.floor.applyResolveAlert", "Apply & resolve alert")}`}
                </Btn>
                <Btn variant="secondary" disabled title={t("workforce.floor.savingARemedyDraftNeedsReq097b", "Saving a remedy draft needs REQ-097b")}>{t("workforce.floor.saveDraft", "Save draft")}</Btn>
                <span style={{ flex: 1 }} />
                <Btn variant="secondary" disabled title={t("workforce.floor.snoozingAnAlertNeedsReq097b", "Snoozing an alert needs REQ-097b")}>{t("workforce.floor.snooze10m", "Snooze 10m")}</Btn>
              </div>
            </div>
          )}
        </Card>
      </div>

      {board.reflow.length > 0 ? (
        <Card>
          <CardHead title={t("workforce.floor.intradayReFlowAppliedToday2", "Intraday re-flow · applied today")} />
          {board.reflow.map((r) => (
            <div key={r.id} data-testid="wffloor-reflow-row" style={{ display: "flex", gap: 10, padding: "10px 18px", borderTop: `1px solid ${T.n6}`, alignItems: "center", fontSize: 12.5, flexWrap: "wrap" }}>
              <b style={{ color: T.n1, width: 140 }}>{r.advisorName}</b>
              <span style={{ color: T.n2 }}>{r.from}</span><span style={{ color: T.soft }}>→</span><b style={{ color: T.n1 }}>{r.to}</b>
              <span style={{ marginLeft: "auto", fontSize: 11.5, color: T.soft }}>{r.action}</span>
            </div>
          ))}
        </Card>
      ) : null}
    </>
  );
}

/* ── Analytics (F3, F4) ───────────────────────────────────────────────── */

function AnalyticsTab({ data, events }: { data: AssistAnalytics; events: AssistEvent[] }) {
  const t = useT();
  const phone = useViewport() === "phone";
  const maxDaily = Math.max(1, ...data.dailyVolume.map((d) => d.count));
  const avgDaily = data.dailyVolume.length ? data.dailyVolume.reduce((s, d) => s + d.count, 0) / data.dailyVolume.length : 0;
  const resolved = events.filter((e) => e.status !== "open").length;
  const escalated = events.filter((e) => e.escalated).length;

  const hourBuckets = useMemo(() => {
    const b: Record<number, number> = {};
    events.forEach((e) => { const h = new Date(e.ts).getHours(); b[h] = (b[h] || 0) + 1; });
    return b;
  }, [events]);
  const hours = Object.keys(hourBuckets).map(Number).sort((a, b) => a - b);
  const maxHour = Math.max(1, ...hours.map((h) => hourBuckets[h]));
  const peakHour = hours.reduce<number | null>((best, h) => (best == null || hourBuckets[h] > hourBuckets[best] ? h : best), null);

  const reasonTtr = useMemo(() => {
    const m: Record<string, { n: number; sum: number }> = {};
    events.forEach((e) => { if (e.ttrSec != null) { const r = m[e.reason] ?? { n: 0, sum: 0 }; r.n += 1; r.sum += e.ttrSec; m[e.reason] = r; } });
    return m;
  }, [events]);

  const locBuckets = useMemo(() => {
    const b: Record<string, number> = {};
    events.forEach((e) => { if (e.location) b[e.location] = (b[e.location] || 0) + 1; });
    return Object.entries(b).sort((a, b2) => b2[1] - a[1]).slice(0, 6);
  }, [events]);
  const maxLoc = Math.max(1, ...locBuckets.map(([, c]) => c));
  const maxReason = Math.max(1, ...data.reasons.map((r) => r.count));
  const maxBanker = Math.max(1, ...data.bankers.map((b) => b.count));
  const hourLabel = (h: number) => String(h).padStart(2, "0");

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: phone ? "1fr 1fr" : "repeat(auto-fit, minmax(170px, 1fr))", gap: 12 }}>
        <Tile accent={T.p1} label={t("workforce.floor.assistsLast30Days", "ASSISTS · THIS PERIOD")}><span style={bigNum}>{data.totalCount}</span><span style={tileFoot}>{t("workforce.floor.deltaNeedsPriorPeriod", "no prior-period comparison served (REQ-097e)")}</span></Tile>
        <Tile accent={T.green1} label={t("workforce.assist.avgTimeToResolve", "AVG TIME-TO-RESOLVE")}><span style={bigNum}>{fmtTtr(data.avgTtrSec)}</span><span style={tileFoot}>{resolved} {t("workforce.floor.resolvedLower", "resolved")}</span></Tile>
        <Tile accent="#4981F2" label={t("workforce.floor.medianTimeToResolve", "MEDIAN TIME-TO-RESOLVE")}><span style={bigNum}>{fmtTtr(data.medianTtrSec)}</span><span style={tileFoot}>{resolved} {t("workforce.floor.resolvedLower", "resolved")}</span></Tile>
        <Tile accent={T.orange1} label={t("workforce.assist.autoFlagged", "AUTO-FLAGGED")}><span style={bigNum}>{data.autoPct}%</span><span style={tileFoot}>{events.filter((e) => e.source === "auto").length} {t("workforce.floor.of", "of")}{" "}{events.length}</span></Tile>
        <Tile accent={T.red1} label={t("workforce.floor.escalatedToFloorLead2", "ESCALATED TO FLOOR LEAD")}><span style={bigNum}>{data.escalatedPct}%</span><span style={tileFoot}>{escalated} · {data.slaPct}% {t("workforce.floor.resolvedUnder5min", "resolved < 5min")}</span></Tile>
      </div>

      <Card>
        <CardHead title={t("workforce.assist.dailyVolume", "Daily volume")} sub={t("workforce.floor.bandShowsWeeklyAverage", "This period · dashed line is the daily average")}
          right={peakHour != null ? <span style={{ fontSize: 11, color: T.soft }}>{t("workforce.floor.peak", "Peak:")}{" "}<b style={{ color: T.n1, fontFamily: T.fontMono }}>{hourLabel(peakHour)}:00</b> · {hourBuckets[peakHour]} {t("workforce.floor.assistsLower", "assists")}</span> : null} />
        <div style={{ padding: "6px 20px 18px", position: "relative", height: 150 }}>
          <div style={{ position: "absolute", left: 20, right: 20, bottom: 40, height: 0, borderTop: `1px dashed ${T.p1}`, transform: `translateY(-${(avgDaily / maxDaily) * 95}px)`, zIndex: 1 }}>
            <span style={{ position: "absolute", right: 0, top: -14, fontSize: 10, color: T.p1, fontFamily: T.fontMono }}>{t("workforce.floor.avg", "avg")}{" "}{avgDaily.toFixed(1)}/{t("workforce.floor.day", "day")}</span>
          </div>
          <div style={{ display: "flex", gap: 4, alignItems: "flex-end", height: 110 }}>
            {data.dailyVolume.map((d, i) => (
              <div key={`${d.label}-${i}`} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, height: "100%", justifyContent: "flex-end" }}>
                <div title={`${d.label} · ${d.count}`} style={{ width: "76%", height: `${Math.max(2, (d.count / maxDaily) * 95)}px`, background: i === data.dailyVolume.length - 1 ? T.p1 : d.count === 0 ? T.n5 : alpha(T.p1, 38), borderRadius: "2px 2px 0 0" }} />
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
            {data.dailyVolume.map((d, i) => (
              <span key={`l-${d.label}-${i}`} style={{ flex: 1, textAlign: "center", fontSize: 9, color: T.soft, whiteSpace: "nowrap", overflow: "hidden" }}>{i % 3 === 0 ? d.label : ""}</span>
            ))}
          </div>
        </div>
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: phone ? "1fr" : "repeat(auto-fit, minmax(320px, 1fr))", gap: 14 }}>
        <Card>
          <CardHead title={t("workforce.assist.mostPagedReasons", "Most-paged reasons")} sub={t("workforce.floor.reasonsLearnFromUsage", "Reasons learn from usage and surface as ‘Recents’ to bankers")} />
          <div style={{ padding: "4px 18px 16px", display: "flex", flexDirection: "column", gap: 11 }}>
            {data.reasons.map((r) => {
              const ttr = reasonTtr[r.label];
              return (
                <div key={r.label}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, gap: 10 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: T.n1 }}>{r.label}</span>
                    <span style={{ fontSize: 11.5, fontFamily: T.fontMono, color: T.soft, whiteSpace: "nowrap" }}>{r.count}{ttr ? ` · ${t("workforce.floor.avg", "avg")} ${fmtTtr(Math.round(ttr.sum / ttr.n))}` : ""}</span>
                  </div>
                  <div style={{ height: 6, borderRadius: 3, background: T.n6, overflow: "hidden" }}><div style={{ height: "100%", width: `${(r.count / maxReason) * 100}%`, background: T.p1 }} /></div>
                </div>
              );
            })}
          </div>
        </Card>
        <Card>
          <CardHead title={t("workforce.floor.timeOfDay", "Time-of-day")} sub={t("workforce.floor.whenAssistsHappen", "When assists happen across the trading day")} />
          <div style={{ padding: "10px 18px 18px", display: "flex", gap: 5, alignItems: "flex-end", height: 130 }}>
            {hours.length === 0 ? <span style={{ fontSize: 12, color: T.soft }}>{t("workforce.floor.noEventsInThisPeriod", "No events in this period.")}</span> : hours.map((h) => (
              <div key={h} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
                <div title={`${hourLabel(h)}:00 · ${hourBuckets[h]}`} style={{ width: "100%", maxWidth: 26, height: Math.round((hourBuckets[h] / maxHour) * 80) + 4, background: T.tTurquoise, borderRadius: 3 }} />
                <span style={{ fontSize: 9.5, color: T.soft, fontFamily: T.fontMono }}>{hourLabel(h)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: phone ? "1fr" : "repeat(auto-fit, minmax(320px, 1fr))", gap: 14 }}>
        <Card>
          <CardHead title={t("workforce.assist.byBanker", "By banker")}
            sub={t("workforce.floor.byBankerCaption", "Who's leaning on the page button. Read as ‘who needs more support’, not as ‘who's struggling’.")}
            right={<Pill tone="neutral">{data.bankers.length} {t("workforce.floor.bankers", "bankers")}</Pill>} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 46px 66px 58px", gap: 8, padding: "8px 18px", background: T.n6, fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.06, color: T.soft }}>
            <span>{t("workforce.floor.banker", "Banker")}</span><span style={{ textAlign: "right" }}>{t("workforce.floor.pages", "Pages")}</span><span style={{ textAlign: "right" }}>{t("workforce.floor.avgTtr", "Avg TTR")}</span><span style={{ textAlign: "right" }}>{t("workforce.assist.escalated", "Escalated")}</span>
          </div>
          {data.bankers.slice(0, 10).map((b) => (
            <div key={b.name} data-testid="wffloor-banker-row" style={{ display: "grid", gridTemplateColumns: "1fr 46px 66px 58px", gap: 8, padding: "9px 18px", alignItems: "center", fontSize: 12, borderTop: `1px solid ${T.n6}` }}>
              <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                <span style={{ width: 22, height: 22, borderRadius: "50%", background: T.p3, color: T.p1, fontSize: 9.5, fontWeight: 700, display: "grid", placeItems: "center", flexShrink: 0 }}>{b.initials}</span>
                <span style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                  <b style={{ color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{b.name}</b>
                  <span style={{ height: 3, borderRadius: 2, background: T.n6, marginTop: 3 }}><span style={{ display: "block", height: "100%", width: `${(b.count / maxBanker) * 100}%`, background: T.p1, borderRadius: 2 }} /></span>
                </span>
              </span>
              <span style={{ fontFamily: T.fontMono, fontWeight: 700, textAlign: "right" }}>{b.count}</span>
              <span style={{ fontFamily: T.fontMono, color: T.n2, textAlign: "right" }}>{fmtTtr(b.avgTtrSec)}</span>
              <span style={{ fontFamily: T.fontMono, color: b.escalated > 0 ? T.red1 : T.soft, textAlign: "right" }}>{b.escalated}</span>
            </div>
          ))}
          {data.bankers.length === 0 ? <EmptyState icon={<Icon.user size={18} />} title={t("workforce.assist.noData", "No data")} body={t("workforce.assist.nothingForThisPeriod", "Nothing for this period.")} /> : null}
        </Card>
        <Card>
          <CardHead title={t("workforce.floor.hotspots", "Hotspots")} sub={t("workforce.floor.pagesByFloorLocation", "Pages by floor location")} />
          <div style={{ padding: "6px 18px 18px", display: "flex", flexDirection: "column", gap: 11 }}>
            {locBuckets.length === 0 ? <span style={{ fontSize: 12, color: T.soft }}>{t("workforce.floor.noEventsInThisPeriod", "No events in this period.")}</span> : locBuckets.map(([loc, c]) => (
              <div key={loc} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 150, fontSize: 12, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{loc}</span>
                <span style={{ flex: 1, height: 7, background: T.n6, borderRadius: 999, overflow: "hidden" }}><span style={{ display: "block", width: `${Math.round((c / maxLoc) * 100)}%`, height: "100%", background: T.tTurquoise }} /></span>
                <span style={{ fontSize: 12, fontFamily: T.fontMono, color: T.n2, width: 26, textAlign: "right" }}>{c}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </>
  );
}

/* ── Audit log (F5, F6) ───────────────────────────────────────────────── */

type AuditFilter = "all" | "open" | "auto" | "manual" | "escalated";
const AUDIT_FILTERS: { id: AuditFilter; label: string; labelKey: string }[] = [
  { id: "all", label: "All", labelKey: "common.filters.all" },
  { id: "open", label: "Open", labelKey: "common.actions.open" },
  { id: "auto", label: "Auto-flagged", labelKey: "workforce.assist.autoFlagged" },
  { id: "manual", label: "Manual", labelKey: "workforce.assist.manual" },
  { id: "escalated", label: "Escalated", labelKey: "workforce.assist.escalated" },
];

function AuditTab({ events, period, initialFilter }: { events: AssistEvent[]; period: Period; initialFilter: AuditFilter }) {
  const t = useT();
  const phone = useViewport() === "phone";
  const [q, setQ] = useState(""); // ui-local
  const [f, setF] = useState<AuditFilter>(initialFilter); // ui-local

  const filtered = events.filter((e) => {
    if (f === "open" && e.status !== "open") return false;
    if (f === "auto" && e.source !== "auto") return false;
    if (f === "manual" && e.source !== "manual") return false;
    if (f === "escalated" && !e.escalated) return false;
    if (!q) return true;
    const needle = q.toLowerCase();
    return e.fromName.toLowerCase().includes(needle) || e.reason.toLowerCase().includes(needle) || e.location.toLowerCase().includes(needle);
  });

  const exportCsv = () => {
    const head = ["When", "Banker", "Reason", "Location", "Source", "Escalated", "TTR seconds", "Status"];
    const rows = filtered.map((e) => [e.ts, e.fromName, e.reason, e.location, e.source, e.escalated ? "yes" : "no", e.ttrSec == null ? "" : String(e.ttrSec), e.status]);
    const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url; a.download = `assist-audit-${period}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const cols = "104px 1fr 132px 96px 64px 82px";
  return (
    <Card>
      <CardHead title={t("workforce.floor.auditLog", "Audit log")} sub={t("workforce.floor.everyAssistEventInThisPeriod", "Every assist event in this period · filterable + exportable")}
        right={<Btn size="sm" variant="secondary" testid="wffloor-export-csv" icon={<Icon.download size={13} />} onClick={exportCsv}>{t("workforce.floor.exportCsv", "Export CSV")}</Btn>} />
      <div style={{ padding: "0 18px 12px", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {AUDIT_FILTERS.map((x) => <Chip key={x.id} active={f === x.id} onClick={() => setF(x.id)}>{t(x.labelKey, x.label)}</Chip>)}
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("workforce.assist.searchBankerReasonLocation", "Search banker, reason, location…")}
          style={{ flex: 1, minWidth: 200, padding: "8px 11px", minHeight: 34, border: `1px solid ${T.n5}`, borderRadius: 8, fontSize: 12.5, fontFamily: T.font, boxSizing: "border-box" }} />
        <span style={{ fontSize: 11.5, color: T.soft, fontFamily: T.fontMono, whiteSpace: "nowrap" }}>{filtered.length} {t("workforce.floor.of", "of")}{" "}{events.length}</span>
      </div>

      {phone ? (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {filtered.slice(0, 100).map((e) => (
            <div key={e.id} data-testid="wffloor-audit-row" style={{ display: "flex", flexDirection: "column", gap: 4, padding: "12px 18px", borderTop: `1px solid ${T.n6}` }}>
              <span style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                <b style={{ fontSize: 13, color: T.n1 }}>{e.fromName}</b>
                {e.status === "open" ? <Pill tone="primary">{t("common.actions.open", "Open")}</Pill> : <Pill tone="success">{t("workforce.assist.resolved", "Resolved")}</Pill>}
              </span>
              <span style={{ fontSize: 12, color: T.n2 }}>{e.reason}</span>
              <span style={{ fontSize: 11, color: T.soft, fontFamily: T.fontMono }}>{new Date(e.ts).toLocaleDateString(undefined, { month: "short", day: "numeric" })} · {clock(e.ts)} · {e.location} · {e.source === "auto" ? t("workforce.assist.auto", "Auto") : t("workforce.assist.manual", "Manual")} · {fmtTtr(e.ttrSec)}</span>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: cols, gap: 10, padding: "9px 18px", background: T.n6, fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.06, color: T.soft }}>
            <span>{t("workforce.assist.when", "When")}</span><span>{t("workforce.assist.bankerReason", "Banker · reason")}</span><span>{t("workforce.assist.location", "Location")}</span><span>{t("workforce.assist.source", "Source")}</span><span style={{ textAlign: "right" }}>{t("workforce.assist.ttr", "TTR")}</span><span style={{ textAlign: "right" }}>{t("workforce.assist.status", "Status")}</span>
          </div>
          {filtered.slice(0, 100).map((e) => (
            <div key={e.id} data-testid="wffloor-audit-row" style={{ display: "grid", gridTemplateColumns: cols, gap: 10, padding: "10px 18px", borderTop: `1px solid ${T.n6}`, alignItems: "center", fontSize: 12 }}>
              <span style={{ color: T.soft, fontFamily: T.fontMono, fontSize: 11 }}>{new Date(e.ts).toLocaleDateString(undefined, { month: "short", day: "numeric" })} · {clock(e.ts)}</span>
              <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                <span style={{ width: 20, height: 20, borderRadius: "50%", background: T.p3, color: T.p1, fontSize: 9, fontWeight: 700, display: "grid", placeItems: "center", flexShrink: 0 }}>{e.fromInitials}</span>
                <span style={{ minWidth: 0 }}><b style={{ color: T.n1 }}>{e.fromName}</b><span style={{ color: T.soft }}> · {e.reason}</span></span>
              </span>
              <span style={{ color: T.n2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.location}</span>
              <span style={{ display: "flex", gap: 4 }}>
                {e.source === "auto" ? <Pill tone="primary">{t("workforce.assist.auto", "Auto")}</Pill> : <Pill tone="neutral">{t("workforce.assist.manual", "Manual")}</Pill>}
                {e.escalated ? <Pill tone="danger">{t("workforce.floor.esc", "Esc.")}</Pill> : null}
              </span>
              <span style={{ textAlign: "right", fontFamily: T.fontMono, color: e.ttrSec == null ? T.soft : T.n2 }}>{fmtTtr(e.ttrSec)}</span>
              <span style={{ textAlign: "right" }}>{e.status === "open" ? <Pill tone="primary">{t("common.actions.open", "Open")}</Pill> : <Pill tone="success">{t("workforce.assist.resolved", "Resolved")}</Pill>}</span>
            </div>
          ))}
        </>
      )}
      {filtered.length === 0 ? <EmptyState icon={<Icon.check size={18} />} title={t("workforce.assist.noMatches", "No matches")} body={t("workforce.assist.tryADifferentSearch", "Try a different search.")} /> : null}
    </Card>
  );
}

/* ── Settings (F7, F8) ────────────────────────────────────────────────── */

function Toggle({ label, help, value, onChange, disabled }: { label: string; help: string; value: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start", opacity: disabled ? 0.5 : 1 }}>
      <button role="switch" aria-checked={value} aria-label={label} disabled={disabled} onClick={() => onChange(!value)}
        style={{ width: 38, height: 22, borderRadius: 11, background: value ? T.p1 : T.n4, border: "none", padding: 2, cursor: disabled ? "not-allowed" : "pointer", flexShrink: 0 }}>
        <span style={{ width: 18, height: 18, borderRadius: "50%", background: T.white, display: "block", transform: value ? "translateX(16px)" : "translateX(0)", transition: "transform 140ms" }} />
      </button>
      <span><span style={{ fontSize: 13, fontWeight: 600, color: T.n1, display: "block" }}>{label}</span><span style={{ fontSize: 11, color: T.soft, lineHeight: 1.45, display: "block" }}>{help}</span></span>
    </div>
  );
}

function SettingsTab() {
  const t = useT();
  const phone = useViewport() === "phone";
  const { data, loading, reload } = useWfResource<AssistSettings>("assist-settings", getAssistSettings);
  const { busy, run } = useBusy();
  const set = (patch: Partial<AssistSettings>) => void run("s", async () => {
    try { await updateAssistSettings(patch); toast({ tone: "success", title: t("workforce.assist.settingsUpdated", "Settings updated") }); reload(); }
    catch (e) { toast({ tone: "danger", title: t("workforce.assist.couldnTUpdate", "Couldn't update"), subtitle: e instanceof Error ? e.message : String(e) }); }
  });
  if (loading || !data) return <div style={{ padding: 40, textAlign: "center", color: T.soft }}>{t("common.status.loading", "Loading…")}</div>;
  const covered = data.coveredBy != null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: phone ? "1fr" : "repeat(auto-fit, minmax(360px, 1fr))", gap: 14 }}>
      <Card>
        <CardHead title={t("workforce.assist.coverage", "Coverage")} sub={t("workforce.floor.whoHandlesAssistsWhenYoureOffTheFloor", "Who handles assists when you're off the floor")} />
        <div style={{ padding: "0 18px 18px", display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.06, color: T.soft }}>{t("workforce.floor.currentStatus", "Current status")}</span>
            <div style={{ marginTop: 6, padding: "12px 14px", border: `1px solid ${covered ? T.orange1 : T.green1}`, background: alpha(covered ? T.orange1 : T.green1, 8), borderRadius: 9, display: "flex", gap: 10, alignItems: "flex-start" }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: covered ? T.orange1 : T.green1, marginTop: 5, flexShrink: 0 }} />
              <span>
                <b style={{ fontSize: 13, color: covered ? T.orange1 : T.green1, display: "block" }}>{covered ? `${t("workforce.floor.coveredBy", "Covered by")} ${data.coveredBy}` : t("workforce.floor.onTheFloor", "On the floor")}</b>
                <span style={{ fontSize: 11.5, color: T.n2 }}>{covered ? t("workforce.floor.newAssistsGoToYourCover", "New assists go to your cover.") : t("workforce.floor.newAssistsComeStraightToYou", "New assists come straight to you.")}</span>
              </span>
            </div>
          </div>
          <div>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: T.n1, display: "block" }}>{t("workforce.floor.setCoverage", "Set coverage")}</span>
            <span style={{ fontSize: 11, color: T.soft, lineHeight: 1.45 }}>{t("workforce.floor.bankersSeeATinyBanner", "Bankers will see a tiny banner in their picker that says where their page is going.")}</span>
          </div>
          <Btn size="sm" testid="wffloor-on-floor" disabled={busy !== null || !covered} onClick={() => set({ coveredBy: null })}>{t("workforce.floor.onFloor", "On floor")}</Btn>
        </div>
      </Card>

      <Card>
        <CardHead title={t("workforce.floor.autoTriggers", "Auto-triggers")} sub={t("workforce.floor.systemGeneratedAssists", "System-generated assists")} />
        <div style={{ padding: "0 18px 18px", display: "flex", flexDirection: "column", gap: 16 }}>
          <Toggle label={t("workforce.assist.text15MinOverrunDelayCoverage", "15-minute overrun → delay coverage")}
            help={t("workforce.floor.overrunHelp", "When a banker's in-progress appointment runs more than 15 min over, auto-flag delay coverage for the next arrival.")}
            value={data.autoFlagOverrun} onChange={(v) => set({ autoFlagOverrun: v })} />
          <Toggle label={t("workforce.assist.text10MinWaitWalkInTriage", "10-minute wait → walk-in triage")}
            help={t("workforce.floor.waitTriageHelp", "When a walk-in has waited 10 minutes with no available banker, raise a manager triage page. EXPERIMENTAL · high-noise risk, off by default.")}
            value={data.autoFlagWaitTime} onChange={(v) => set({ autoFlagWaitTime: v })} />
          <div>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: T.n1, display: "flex", justifyContent: "space-between" }}>
              {t("workforce.assist.escalateToFloorLeadAfter", "Escalate to floor lead after")}<span style={{ fontFamily: T.fontMono }}>{data.escalateAfterSec}s</span>
            </span>
            <input type="range" min={30} max={120} step={15} value={data.escalateAfterSec} onChange={(e) => set({ escalateAfterSec: parseInt(e.target.value, 10) })} style={{ width: "100%", marginTop: 8, accentColor: T.p1 }} />
            <span style={{ fontSize: 11, color: T.soft }}>{t("workforce.floor.escalateHelp", "Default 60s. Tune from data; if escalations are rare, lower it.")}</span>
          </div>
        </div>
      </Card>

      <Card>
        <CardHead title={t("workforce.assist.mobilePush", "Mobile push")} sub={t("workforce.floor.whatFiresOnYourPhone", "What fires on your phone")} />
        <div style={{ padding: "0 18px 18px", display: "flex", flexDirection: "column", gap: 16 }}>
          <Toggle label={t("workforce.assist.pushForEveryAssist", "Push for every assist")} help={t("workforce.floor.pushAllHelp", "Recommended on. Bankers expect a fast response.")} value={data.pushOnAll} onChange={(v) => set({ pushOnAll: v })} />
          <Toggle label={t("workforce.assist.alwaysPushHighSeverity", "Always push high-severity")} help={t("workforce.floor.pushHighHelp", "Aggressive customer and suspected fraud — pushes through even if ‘push for every assist’ is off.")} value={data.pushOnHigh} onChange={(v) => set({ pushOnHigh: v })} disabled={data.pushOnAll} />
          <div style={{ paddingTop: 4, borderTop: `1px solid ${T.n6}` }}>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: T.n1, display: "block", marginTop: 12 }}>{t("workforce.floor.notificationSound", "Notification sound")}</span>
            <span style={{ fontSize: 11, color: T.soft }}>{t("workforce.floor.soundHeld", "Not served yet — REQ-097f. Phone respects Do Not Disturb either way.")}</span>
          </div>
        </div>
      </Card>

      <Card>
        <CardHead title={t("workforce.floor.reasonPresets", "Reason presets")} sub={t("workforce.floor.whatAppearsUnderSuggested", "What appears under ‘Suggested’ in the banker's picker")} />
        <WfPending opName="getAssistReasonPresets" screen="wffloor" />
      </Card>
    </div>
  );
}

/* ── Screen ───────────────────────────────────────────────────────────── */

export default function WorkforceFloor() {
  const t = useT();
  const u = useUser();
  const [tab, setTab] = useState<Tab>("live"); // ui-local
  const [period, setPeriod] = useState<Period>("30d"); // ui-local
  const [auditFilter, setAuditFilter] = useState<AuditFilter>("all"); // ui-local
  const [bannerOff, setBannerOff] = useState(false); // ui-local

  // §3.2: branch comes from the membership's locations or the session's persona
  // assignment — no tenant-named fallback.
  const locationId = u.locations?.[0] ?? sessionScope().branchId;
  const floor = useWfResource<FloorBoardResponse>(
    `floor:${locationId ?? "none"}`,
    () => locationId ? getFloorBoard(locationId) : Promise.reject(new Error("This session has no branch assignment — the floor board needs one (REQ-090: branchId on the session snapshot).")),
    { pollMs: 30000 },
  );
  const analytics = useWfResource<AssistAnalytics>(`assist-an:${period}`, () => getAssistAnalytics(period));
  const events = useWfResource<{ items: AssistEvent[] }>(`assist-ev:${period}`, () => listAssistEvents(period));
  const settings = useWfResource<AssistSettings>("assist-settings", getAssistSettings);

  const openEvents = (floor.data?.assistFeed ?? []).filter((e) => e.status === "open");
  const banner = openEvents.length > 0
    ? openEvents.reduce((oldest, e) => (new Date(e.ts).getTime() < new Date(oldest.ts).getTime() ? e : oldest), openEvents[0])
    : null;

  const showPeriod = tab === "analytics" || tab === "audit";

  return (
    <Screen rail="wf-floor">
      {banner && !bannerOff ? (
        <EscalationBanner ev={banner} escalateAfterSec={settings.data?.escalateAfterSec ?? null}
          onOpenTray={() => { setAuditFilter("open"); setTab("audit"); }} onDismiss={() => setBannerOff(true)} />
      ) : null}
      <DataSourceStrip screen="wffloor" ops={["getFloorBoard", "resolveFloorAlert", "getLiveQueues", "listMarketplaceShifts", "getDemandForecast", "getAssistAnalytics", "listAssistEvents", "getAssistSettings", "updateAssistSettings"]} reqId="REQ-039" />
      <div style={{ flex: 1, overflow: "auto", padding: "14px 24px 32px", display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <div style={{ display: "flex", gap: 4, background: T.n6, borderRadius: 9, padding: 3 }}>
            <TabBtn active={tab === "live"} onClick={() => setTab("live")} testid="wffloor-tab-live">
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: tab === "live" ? T.white : T.red1 }} />
              {t("workforce.floor.live", "Live")}
              <span style={{ fontFamily: T.fontMono, fontSize: 11, opacity: 0.85 }}>{floor.data?.alerts.length ?? ""}</span>
            </TabBtn>
            <TabBtn active={tab === "analytics"} onClick={() => setTab("analytics")} testid="wffloor-tab-analytics">{t("workforce.floor.analytics", "Analytics")}</TabBtn>
            <TabBtn active={tab === "audit"} onClick={() => setTab("audit")} testid="wffloor-tab-audit">
              {t("workforce.floor.auditLog", "Audit log")}
              <span style={{ fontFamily: T.fontMono, fontSize: 11, opacity: 0.85 }}>{events.data?.items.length ?? ""}</span>
            </TabBtn>
            <TabBtn active={tab === "settings"} onClick={() => setTab("settings")} testid="wffloor-tab-settings">{t("common.nav.settings", "Settings")}</TabBtn>
          </div>
          {showPeriod ? (
            <div style={{ display: "flex", gap: 4 }}>
              {PERIODS.map((p) => <Chip key={p.id} active={period === p.id} onClick={() => setPeriod(p.id)}>{t(p.labelKey, p.label)}</Chip>)}
            </div>
          ) : null}
        </div>

        {tab === "live" ? (
          floor.error ? <WfPending opName="getFloorBoard" error={floor.error} onRetry={floor.reload} screen="wffloor" />
            : floor.loading || !floor.data ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("workforce.floor.loadingTheFloor", "Loading the floor…")}</div>
            : <LiveTab board={floor.data} locationId={locationId ?? ""} onReload={floor.reload} />
        ) : tab === "analytics" ? (
          analytics.loading || !analytics.data ? <div style={{ padding: 40, textAlign: "center", color: T.soft }}>{t("common.status.loading", "Loading…")}</div>
            : <AnalyticsTab data={analytics.data} events={events.data?.items ?? []} />
        ) : tab === "audit" ? (
          events.loading || !events.data ? <div style={{ padding: 40, textAlign: "center", color: T.soft }}>{t("common.status.loading", "Loading…")}</div>
            : <AuditTab events={events.data.items} period={period} initialFilter={auditFilter} />
        ) : <SettingsTab />}
      </div>
    </Screen>
  );
}
